<template>
  <div class="duration-pills">
    <div class="duration-pills__row">
      <segmented-toggle
        variant="pills"
        size="sm"
        :value="activeValue"
        :options="options"
        :disabled="disabled || adding"
        @input="onSelect"
      />
      <base-button v-if="!adding" size="sm" variant="outlined" tone="primary" :disabled="disabled" @click="startAdd">
        <i class="tim-icons icon-simple-add"></i> Add duration
      </base-button>
      <div v-else class="duration-pills__add">
        <base-input class="mb-0 form-field--compact">
          <el-input-number
            ref="minutesField"
            v-model="newMinutes"
            :min="5"
            :step="5"
            :controls="false"
            size="small"
            placeholder="Minutes"
            @keydown.enter.native.prevent="confirmAdd"
            @keydown.esc.native.prevent="cancelAdd"
          />
        </base-input>
        <span class="duration-pills__unit">min</span>
        <button class="row-remove" type="button" aria-label="Add this duration" title="Add" @click="confirmAdd">
          <i class="tim-icons icon-check-2"></i>
        </button>
        <button class="row-remove" type="button" aria-label="Cancel" title="Cancel" @click="cancelAdd">
          <i class="tim-icons icon-simple-remove"></i>
        </button>
      </div>
    </div>
    <small v-if="addError" class="text-danger d-block mt-1">{{ addError }}</small>
    <div v-if="!adding && activeDuration && !activeDuration.is_default" class="duration-pills__links">
      <button class="duration-pills__link" type="button" :disabled="disabled" @click="emit('set-default', Number(activeDuration.duration_minutes))">
        Set {{ activeDuration.duration_minutes }} min as the default
      </button>
      <span class="duration-pills__dot" aria-hidden="true">&middot;</span>
      <button class="duration-pills__link" type="button" :disabled="disabled" @click="confirmRemove.show = true">
        Remove this duration
      </button>
    </div>
    <base-alert-dialog
      :show="confirmRemove.show"
      title="Remove duration"
      :message="removeMessage"
      variant="warning"
      show-cancel
      confirm-text="Remove"
      confirm-variant="danger"
      @confirm="doRemove"
      @cancel="confirmRemove.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, type PropType } from 'vue'
import { sortDurations } from '@/util/pricing'

interface DurationEntry {
  duration_minutes: number
  is_default: boolean | number
  position?: number
}

// Runtime declaration: a TS-only `value: number | null` compiles to a required [Number, null]
// prop, and Vue 2 then rejects the null that means "default duration selected".
const props = defineProps({
  durations: { type: Array as PropType<DurationEntry[]>, default: () => [] },
  value: { type: Number as PropType<number | null>, default: null },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits<{
  (e: 'input', minutes: number | null): void
  (e: 'add', minutes: number): void
  (e: 'remove', minutes: number): void
  (e: 'set-default', minutes: number): void
}>()

const DEFAULT_KEY = 'default'

// Same order as every other surface: ascending by minutes
const sorted = computed(() => sortDurations(props.durations || []) as DurationEntry[])

const options = computed(() => sorted.value.map(d => ({
  value: d.is_default ? DEFAULT_KEY : Number(d.duration_minutes),
  label: `${d.duration_minutes} min${d.is_default ? ' (default)' : ''}`
})))

const activeValue = computed(() => (props.value === null || props.value === undefined ? DEFAULT_KEY : Number(props.value)))

const activeDuration = computed(() => {
  if (props.value === null || props.value === undefined) {
    return sorted.value.find(d => !!d.is_default) || null
  }
  return sorted.value.find(d => Number(d.duration_minutes) === Number(props.value)) || null
})

function onSelect(value: string | number) {
  emit('input', value === DEFAULT_KEY ? null : Number(value))
}

const adding = ref(false)
const newMinutes = ref<number | undefined>(undefined)
const addError = ref('')
const minutesField = ref<any>(null)

function startAdd() {
  adding.value = true
  newMinutes.value = undefined
  addError.value = ''
  nextTick(() => {
    const el = minutesField.value && minutesField.value.$el ? minutesField.value.$el.querySelector('input') : null
    if (el) el.focus()
  })
}

function cancelAdd() {
  adding.value = false
  addError.value = ''
}

function confirmAdd() {
  const minutes = Number(newMinutes.value)
  if (!Number.isInteger(minutes) || minutes <= 0) {
    addError.value = 'Enter the duration in whole minutes'
    return
  }
  if ((props.durations || []).some(d => Number(d.duration_minutes) === minutes)) {
    addError.value = 'That duration already exists'
    return
  }
  addError.value = ''
  adding.value = false
  emit('add', minutes)
}

const confirmRemove = reactive({ show: false })

const removeMessage = computed(() => {
  const d = activeDuration.value
  return d ? `Remove the ${d.duration_minutes} minute duration and its pricing categories?` : ''
})

function doRemove() {
  confirmRemove.show = false
  if (activeDuration.value) emit('remove', Number(activeDuration.value.duration_minutes))
}
</script>

<script lang="ts">
export default { name: 'DurationPills' }
</script>

<style lang="scss" scoped>
.duration-pills__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.duration-pills__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.duration-pills__unit {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.duration-pills__links {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.duration-pills__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;

  &:hover { color: #e14eca; }
  &:disabled { opacity: 0.5; cursor: default; }
}

.duration-pills__dot {
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
}
</style>

<style lang="scss">
.white-content .duration-pills {
  .duration-pills__unit, .duration-pills__link { color: #8898aa; }
  .duration-pills__link:hover { color: #e14eca; }
  .duration-pills__dot { color: #b0b8c4; }
}
</style>
